import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Loader2, ShieldAlert, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { getDispatchNotePurgeEligibility, purgeDispatchNote } from '../lib/api';

const MIN_REASON = 10;

export const DispatchNotePurgeDialog = ({ dispatchId, dispatchNumber, open, onClose, onPurged }) => {
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmNumber, setConfirmNumber] = useState('');
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    if (!dispatchId) return;
    setLoading(true);
    try {
      const { data } = await getDispatchNotePurgeEligibility(dispatchId);
      setVerdict(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not check purge eligibility');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [dispatchId, onClose]);

  useEffect(() => {
    if (open) {
      setVerdict(null);
      setReason('');
      setConfirmNumber('');
      load();
    }
  }, [open, load]);

  const note = verdict || {};
  const eligible = !!verdict?.eligible;
  const targetNumber = note.dispatch_number || dispatchNumber || '';
  const confirmMatches = confirmNumber.trim().toLowerCase() === String(targetNumber).trim().toLowerCase();
  const canPurge = eligible && reason.trim().length >= MIN_REASON && confirmMatches && !purging;

  const handlePurge = async () => {
    setPurging(true);
    try {
      const { data } = await purgeDispatchNote(dispatchId, reason.trim(), confirmNumber.trim());
      toast.success(data.message || `Dispatch note ${targetNumber} purged`);
      onPurged?.(dispatchId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="dispatch-note-purge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" />
            Purge Dispatch Note {targetNumber}
          </DialogTitle>
          <DialogDescription>
            Soft-deletes a standalone dispatch note raised in error. The note stays recoverable
            from Deleted Items and the reason is written to the compliance audit trail.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking eligibility...
          </div>
        ) : verdict ? (
          <div className="space-y-4">
            <div className="bg-muted/50 border rounded-lg p-3 grid grid-cols-2 gap-y-1.5 text-sm">
              <p><span className="text-muted-foreground">Note #:</span> <span className="font-mono font-medium">{note.dispatch_number}</span></p>
              <p><span className="text-muted-foreground">Status:</span> <span className="font-medium">{note.status}</span></p>
              <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{note.customer_name || '—'}</span></p>
              <p><span className="text-muted-foreground">PO:</span> <span className="font-mono">{note.po_number || '—'}</span></p>
              <p><span className="text-muted-foreground">Lines:</span> {note.lines_count}</p>
              <p><span className="text-muted-foreground">Batch lines:</span> {note.batch_lines_count}</p>
            </div>

            {eligible ? (
              <div
                className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 flex gap-2"
                data-testid="purge-eligible-banner"
              >
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                <span>
                  Standalone note, never invoiced, no batch details and no returns — safe to purge.
                </span>
              </div>
            ) : (
              <div
                className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900 space-y-1.5"
                data-testid="purge-blockers"
              >
                <p className="font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  Purge blocked — resolve first:
                </p>
                <ul className="list-disc list-inside ml-1 space-y-1">
                  {(note.blockers || []).map((b) => (
                    <li key={b.code} data-testid={`purge-blocker-${b.code}`}>{b.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Label htmlFor="dn-purge-reason" className="font-semibold">Reason for purge *</Label>
              <Textarea
                id="dn-purge-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Duplicate note raised in error for the same delivery"
                className="mt-1"
                rows={3}
                disabled={!eligible}
                data-testid="input-dn-purge-reason"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum {MIN_REASON} characters ({reason.trim().length}/{MIN_REASON}). Logged for compliance.
              </p>
            </div>

            <div>
              <Label htmlFor="dn-purge-confirm" className="font-semibold">
                Type <span className="font-mono">{targetNumber}</span> to confirm *
              </Label>
              <Input
                id="dn-purge-confirm"
                value={confirmNumber}
                onChange={(e) => setConfirmNumber(e.target.value)}
                placeholder={targetNumber}
                className="mt-1 font-mono"
                disabled={!eligible}
                data-testid="input-dn-purge-confirm"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="btn-dn-purge-cancel">Cancel</Button>
          <Button
            variant="destructive"
            onClick={handlePurge}
            disabled={!canPurge}
            data-testid="btn-dn-purge-confirm"
          >
            {purging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Purge Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DispatchNotePurgeDialog;
